import React, { ReactElement } from "react";

import styled from "styled-components";

import {
  CardProjectLine,
  EntityDots,
  Placeholder,
  StyledTitleBlock,
} from "./shared";
import { NoteIcon } from "../../../components/Icons";
import { SkeletonLoader } from "../../../components/Loaders";
import { StackedCardRow as StackRow } from "../../../components/StackedCard";
import { Body2 } from "../../../components/Typography";

/**
 * NoteCard — the new fifth type. A multi-line text card (Milanote-style): a
 * title row that owns the "…" menu, then padded body rows. The body clamps at a
 * line limit and fades out; auto-height, so the card grows with its content.
 * Inline editing lands in task 4/8 — this is the read view + placeholder.
 */

/* The body clamps at a max height and fades out with a mask so long notes end
   softly instead of hard-cropping. white-space: pre-line keeps authored breaks. */
const StyledNoteBody = styled(Body2)`
  display: block;
  max-height: 132px;
  overflow: hidden;
  white-space: pre-line;
  -webkit-mask-image: linear-gradient(180deg, #000 72%, transparent);
  mask-image: linear-gradient(180deg, #000 72%, transparent);
`;

export const SAMPLE_NOTE = `Kickoff open questions before we lock the columns:

• Who owns the migration off react-dnd, and does it block the canvas work or run in parallel?
• Confirm the outline-placeholder choreography reads the same in a scrolled column.
• Do we ship the note type in v1, or defer inline editing?`;

export interface NoteCardProps {
  title?: string;
  body?: string;
  /**
   * The project the note belongs to, above the title — the same line, in the
   * same treatment, that a TaskCard uses. Off by default: only a surface that
   * mixes projects needs it.
   */
  showProjectName?: boolean;
  projectName?: string;
}

export const NoteCard = ({
  title = "Kickoff notes",
  body = SAMPLE_NOTE,
  showProjectName = false,
  projectName = "Marketing Site",
}: NoteCardProps): ReactElement => (
  <>
    <StackRow role="title">
      <StyledTitleBlock>
        {showProjectName ? (
          <CardProjectLine>{projectName}</CardProjectLine>
        ) : null}
        <Body2 weight="bold">{title}</Body2>
      </StyledTitleBlock>
      <EntityDots aria-label="Note options" />
    </StackRow>
    <StackRow role="custom" direction="column">
      <StyledNoteBody color="secondary">{body}</StyledNoteBody>
    </StackRow>
  </>
);

/* ---- placeholder + skeleton ---- */

export const NotePlaceholder = (
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
): ReactElement => (
  <Placeholder icon={<NoteIcon />} label="Write a note" {...props} />
);

/* Skeleton mirrors the note anatomy: a title line, then several body lines. */
export const NoteSkeleton = (): ReactElement => (
  <>
    <StackRow role="title">
      <SkeletonLoader style={{ height: 14, width: "50%" }} />
    </StackRow>
    <StackRow role="custom" direction="column">
      <SkeletonLoader style={{ height: 10, width: "100%" }} />
      <SkeletonLoader style={{ height: 10, width: "96%" }} />
      <SkeletonLoader style={{ height: 10, width: "88%" }} />
      <SkeletonLoader style={{ height: 10, width: "70%" }} />
    </StackRow>
  </>
);
